"use client";

// Inline dialog primitives — a drop-in replacement for the shadcn/Radix
// Dialog + AlertDialog that renders IN THE PAGE FLOW instead of a portal
// overlay.
//
// Why: modal overlays are blocked or mis-rendered by some browsers, and the
// product rule is "no popup windows anywhere in the interface". Every panel
// now lives inside the single-page container, appears in flow (async-synced
// with the data around it), and can be dismissed with its own close control.
//
// The exported names and props match @/components/ui/dialog and
// @/components/ui/alert-dialog, so components only need to change the import.
// Nothing here is portaled, fixed-positioned or backdrop-covered.

import * as React from "react";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Shared open/close state
// ---------------------------------------------------------------------------

type InlineCtx = {
  open: boolean;
  setOpen: (v: boolean) => void;
};

const Ctx = React.createContext<InlineCtx | null>(null);
const useInline = () => React.useContext(Ctx);

function useControllableOpen(props: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [inner, setInner] = React.useState(!!props.defaultOpen);
  const isControlled = props.open !== undefined;
  const open = isControlled ? !!props.open : inner;
  const setOpen = React.useCallback(
    (v: boolean) => {
      if (!isControlled) setInner(v);
      props.onOpenChange?.(v);
    },
    [isControlled, props.onOpenChange]
  );
  return { open, setOpen };
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

function Dialog({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}) {
  const state = useControllableOpen({ open, defaultOpen, onOpenChange });
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

function DialogTrigger({
  children,
  asChild,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const ctx = useInline();
  const open = () => ctx?.setOpen(true);
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<any>;
    return React.cloneElement(child, {
      onClick: (e: React.MouseEvent) => {
        child.props?.onClick?.(e);
        open();
      },
    });
  }
  return (
    <button type="button" onClick={open} {...props}>
      {children}
    </button>
  );
}

function DialogPortal({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

function DialogOverlay() {
  return null;
}

function DialogClose({
  children,
  asChild,
  className,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const ctx = useInline();
  const close = (e: React.MouseEvent) => {
    ctx?.setOpen(false);
  };
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<any>;
    return React.cloneElement(child, {
      onClick: (e: React.MouseEvent) => {
        child.props?.onClick?.(e);
        close(e);
      },
    });
  }
  return (
    <button type="button" className={className} onClick={close} {...props}>
      {children}
    </button>
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<"div"> & { showCloseButton?: boolean }) {
  const ctx = useInline();
  const ref = React.useRef<HTMLDivElement>(null);

  // Bring the panel into view when it opens (instead of stealing the page with
  // a modal). "nearest" only scrolls when it is actually off-screen.
  React.useEffect(() => {
    if (ctx?.open) {
      const id = window.setTimeout(
        () => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
        60
      );
      return () => window.clearTimeout(id);
    }
  }, [ctx?.open]);

  if (!ctx?.open) return null;

  return (
    <section
      ref={ref}
      data-slot="inline-panel"
      role="region"
      className={cn(
        "relative grid w-full gap-4 rounded-2xl border bg-card p-5 text-card-foreground shadow-sm sm:p-6",
        "animate-in fade-in-0 slide-in-from-top-1 duration-200",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <button
          type="button"
          onClick={() => ctx.setOpen(false)}
          className="absolute top-3.5 right-3.5 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Close panel"
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </section>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="inline-panel-header"
      className={cn("flex flex-col gap-1.5 text-left pr-8", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="inline-panel-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="inline-panel-title"
      className={cn("text-base leading-tight font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="inline-panel-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// AlertDialog (inline confirmation strip)
// ---------------------------------------------------------------------------

function AlertDialog({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {children}
    </Dialog>
  );
}

function AlertDialogTrigger(props: any) {
  return <DialogTrigger {...props} />;
}
function AlertDialogPortal({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}
function AlertDialogOverlay() {
  return null;
}
function AlertDialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <DialogContent
      showCloseButton={false}
      className={cn(
        "gap-3 rounded-xl border-rose-300/60 bg-rose-50/60 dark:border-rose-900/60 dark:bg-rose-950/20",
        className
      )}
      {...props}
    >
      {children}
    </DialogContent>
  );
}
function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <DialogHeader className={className} {...props} />;
}
function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <DialogFooter className={className} {...props} />;
}
function AlertDialogTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <h2
      className={cn("text-base leading-tight font-semibold", className)}
      {...props}
    />
  );
}
function AlertDialogDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  onClick,
  ...props
}: React.ComponentProps<"button">) {
  const ctx = useInline();
  return (
    <button
      type="button"
      className={cn(buttonVariants(), className)}
      onClick={(e) => {
        onClick?.(e);
        ctx?.setOpen(false);
      }}
      {...props}
    />
  );
}

function AlertDialogCancel({
  className,
  onClick,
  ...props
}: React.ComponentProps<"button">) {
  const ctx = useInline();
  return (
    <button
      type="button"
      className={cn(buttonVariants({ variant: "outline" }), className)}
      onClick={(e) => {
        onClick?.(e);
        ctx?.setOpen(false);
      }}
      {...props}
    />
  );
}

export {
  // Dialog API
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  // AlertDialog API
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
